import React from 'react';
import clsx from 'clsx';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import NavAppbar from '../nav-top/NavAppbar';
import { getUserState } from '../../features/user/selectors';
import { getProjectState } from '../../features/project/selectors';
import { fetchProject } from '../../features/project/actions';
import { documentTypeToUrl } from '../../scripts/utils';
import { getQueryToolInterfaceToken, getQueryToolProjects } from '../../models/api/query-tool';
import {
  CENTERLINE_AI_CHAT_URL,
  centerlineAiPath,
  centerlineAiProjectFrom,
  useCenterlineAiVisible,
  useIsSiteAdmin,
} from './centerline-ai-access';
import useCenterlineAiStyles from './styles';
import { CenterlineAiProvider, useCenterlineAi } from './CenterlineAiProvider';
import { ProjectSelector } from './ProjectSelector';
import ChatsAndConversation from './ChatsAndConversation';
import { ChatGlyph } from './Glyphs';

/**
 * CENTERLINE AI, CENTERLINE's own chat over the Query Tool (docs/superpowers/plans/
 * 2026-10-15-centerline-ai-native-chat.md; behaviour per docs/query-tool/chat-ui-spec.md). Routed at
 * /main/centerline-ai (CENTERLINE_AI_PATH), not under a project, only in builds with
 * REACT_APP_ENABLE_CENTERLINE_AI=true. The project is in the query string (`?project=`,
 * centerlineAiPath): a link opens on the project it names (or says CENTERLINE AI isn't available
 * for it), a bare address on the project open in CENTERLINE, and picking a project moves the query
 * string, so Back returns to the previous one. Switching never remounts the page: the path doesn't
 * change, so PrivateRoute's project loader doesn't run.
 * The backend decides who may use it (QueryToolService) and issues the tokens the chat calls the
 * Query Tool with. Anyone the frontend doesn't show CENTERLINE AI to (useCenterlineAiVisible: site
 * admins only, for now) who types its address is sent to /main, without a request or a word about
 * CENTERLINE AI.
 */

function CenterlineAiLayout() {
  const classes = useCenterlineAiStyles();
  const theme = useTheme();
  // Below md the chats sidebar would squeeze the conversation, so they open over it instead.
  // noSsr: read the width on the first render, so a phone never draws the sidebar first.
  const narrow = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const { projects, routeUnavailable } = useCenterlineAi();
  const none = routeUnavailable || (projects.status === 'ready' && projects.list.length === 0);

  return (
    <main className={classes.page}>
      <NavAppbar />
      <header className={classes.header}>
        <div className={classes.titleBlock}>
          <h1 className={classes.title}>CENTERLINE AI</h1>
          <div className={classes.subtitle}>
            <span className={classes.statusDot} aria-hidden />
            Ask about the selected project
          </div>
        </div>
        {projects.status === 'ready' && projects.list.length === 0 ? null : (
          <div className={classes.scope}>
            <ProjectSelector />
          </div>
        )}
      </header>
      <div className={classes.body}>
        {none ? (
          <section className={clsx(classes.card, classes.conversation)}>
            <div className={classes.notice} role="status">
              <div className={classes.welcomeIcon} aria-hidden>
                <ChatGlyph />
              </div>
              <h2 className={classes.welcomeTitle}>
                {routeUnavailable
                  ? 'CENTERLINE AI is not available for this project'
                  : 'CENTERLINE AI is not available yet'}
              </h2>
              <p className={classes.welcomeText}>
                {routeUnavailable
                  ? 'It has to be switched on for the project, and your access has to include it.'
                  : 'None of the projects you can open has CENTERLINE AI switched on.'}
              </p>
            </div>
          </section>
        ) : (
          <ChatsAndConversation narrow={narrow} />
        )}
      </div>
    </main>
  );
}

export default function CenterlineAiPage() {
  const { search } = useLocation();
  const history = useHistory();
  const urlProjectId = centerlineAiProjectFrom(search);
  const dispatch = useDispatch();
  const user = useSelector(getUserState);
  const currentProject = useSelector(getProjectState);
  const visible = useCenterlineAiVisible();
  const siteAdmin = useIsSiteAdmin();
  const classes = useCenterlineAiStyles();

  // Until the signed-in user has loaded, nothing; then only those the frontend shows it to.
  if (!user?.id) return null;
  if (!visible) return <Redirect to="/main" />;
  if (!CENTERLINE_AI_CHAT_URL) {
    return (
      <main className={classes.page}>
        <NavAppbar />
        <section className={clsx(classes.card, classes.conversation)}>
          <div className={classes.notice} role="status">
            <h2 className={classes.welcomeTitle}>CENTERLINE AI isn&apos;t configured</h2>
            <p className={classes.welcomeText}>This environment has no CENTERLINE AI service.</p>
          </div>
        </section>
      </main>
    );
  }
  return (
    <CenterlineAiProvider
      baseUrl={CENTERLINE_AI_CHAT_URL}
      // The project the link names; without one, the project open in CENTERLINE.
      routeProjectId={urlProjectId}
      currentProjectId={currentProject?.id}
      // A pick moves the address (a new history entry, so Back returns to the previous project).
      onProjectChosen={(id) => history.push(centerlineAiPath(id))}
      // Whatever selected it (the link, a pick, Back), the selected project is the one open in
      // CENTERLINE too, so the drawer and every other page follow it, and a bare address gets it.
      onProjectSelected={(id) => {
        if (!urlProjectId) history.replace(centerlineAiPath(id));
        if (id !== currentProject?.id) dispatch(fetchProject(id));
      }}
      issueToken={getQueryToolInterfaceToken}
      listProjects={getQueryToolProjects}
      slugs={documentTypeToUrl}
      showTools={siteAdmin}
    >
      <CenterlineAiLayout />
    </CenterlineAiProvider>
  );
}
